import React, { useEffect, useRef, useState } from 'react'
import { Check, Loader2, MoreHorizontal } from 'lucide-react'
import '../styles/LoadingStages.css'

/**
 * LoadingStages - staged loading screen: logo + progress bar + checklist.
 *
 * The progression is time-choreographed so the user always sees movement:
 * one stage is active at a time and advances after ~stageDurationMs (or as
 * soon as its real fetch finished, with a minimum dwell so the change is
 * visible). The bar creeps continuously inside the active stage and caps at
 * 97% — the parent unmounts this screen when the real data is ready.
 *
 * @param {Array}  stages          - [{ key?, label, icon?, done? }] in display order;
 *                                   icon is a lucide-react component and persists across
 *                                   states (only the circle background changes)
 * @param {string} title           - heading under the logo
 * @param {string} logoSrc         - logo path (default '/images/logo_blue.png')
 * @param {number} logoHeight      - logo height in px (default 56)
 * @param {number} stageDurationMs - visual time per stage (default 1800)
 */
function LoadingStages({ stages = [], title = 'Cargando…', logoSrc = '/images/logo_blue.png', logoHeight = 56, stageDurationMs = 1800 }) {
  const MIN_DWELL_MS = 600
  const TICK_MS = 150

  const [progress, setProgress] = useState({ index: 0, percent: 4 })
  const indexRef = useRef(0)
  const stageStartRef = useRef(Date.now())
  const stagesRef = useRef(stages)
  stagesRef.current = stages

  useEffect(() => {
    const timer = setInterval(() => {
      const list = stagesRef.current
      const total = list.length || 1
      let index = indexRef.current
      let elapsed = Date.now() - stageStartRef.current
      const current = list[index] || {}

      // Advance on time, or early when the stage's real fetch already landed.
      // The last stage never completes visually: the parent unmounts us.
      const advance = index < total - 1 &&
        (elapsed >= stageDurationMs || (current.done && elapsed >= MIN_DWELL_MS))

      if (advance) {
        index += 1
        indexRef.current = index
        stageStartRef.current = Date.now()
        elapsed = 0
      }

      // Target from the stage choreography…
      const stageFraction = Math.min(0.95, elapsed / stageDurationMs)
      const target = ((index + stageFraction) / total) * 100
      // …but the bar also creeps steadily (~1.2%/s) so it never looks frozen,
      // like the old LoadingProgress did. Monotonic, capped at 97%.
      setProgress(prev => {
        const creep = prev.percent + 0.18
        const percent = Math.min(97, Math.max(creep, target, 4))
        return { index, percent: Math.max(prev.percent, percent) }
      })
    }, TICK_MS)

    return () => clearInterval(timer)
  }, [stageDurationMs])

  return (
    <div className="stage-loading">
      <img className="stage-loading-logo" src={logoSrc} alt="Logo" height={logoHeight} />
      <div className="stage-loading-title">{title}</div>

      <div className="stage-steps">
        {stages.map((stage, index) => {
          const state = index < progress.index ? 'done' : index === progress.index ? 'active' : 'pending'
          const StageIcon = stage.icon
          const iconSize = state === 'active' ? 26 : state === 'done' ? 20 : 16
          return (
            <div key={stage.key || stage.label} className={`stage-step stage-${state}`}>
              <div className="stage-icon-wrap">
                <span className="stage-icon">
                  {StageIcon
                    ? <StageIcon size={iconSize} strokeWidth={2.2} />
                    : <React.Fragment>
                        {state === 'done' && <Check size={22} strokeWidth={3} />}
                        {state === 'active' && <Loader2 size={30} strokeWidth={2.5} className="stage-spin" />}
                        {state === 'pending' && <MoreHorizontal size={17} strokeWidth={2.5} />}
                      </React.Fragment>}
                </span>
              </div>
              <span className="stage-label">{stage.label}</span>
            </div>
          )
        })}
      </div>

      <div
        className="stage-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress.percent)}
      >
        <div className="stage-progress-fill" style={{ width: `${progress.percent}%` }} />
      </div>
      <div className="stage-progress-text">{Math.round(progress.percent)}%</div>
    </div>
  )
}

export default LoadingStages
